/**
 * Frontend password encryption utility
 * Encrypts sensitive data before sending to API using XOR with hex encoding
 */

const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || "orchy-secure-key";

/**
 * XOR encrypt with hex encoding for binary-safe transmission
 * Note: This is for obfuscation/protection in transit. Real security relies on HTTPS.
 */
export async function encryptPassword(password: string): Promise<string> {
  try {
    const timestamp = Date.now().toString();
    const salt = Math.random().toString(36).substring(2, 10);
    
    const payload = JSON.stringify({
      p: password,
      t: timestamp,
      s: salt,
    });

    // XOR encrypt with key
    let encrypted = "";
    for (let i = 0; i < payload.length; i++) {
      const payloadChar = payload.charCodeAt(i);
      const keyChar = ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
      const xorByte = payloadChar ^ keyChar;
      // Convert to hex for reliable transmission
      encrypted += xorByte.toString(16).padStart(2, "0");
    }

    return encrypted;
  } catch (error) {
    console.error("Password encryption failed:", error);
    // Fallback to base64 if encryption fails
    return btoa(password);
  }
}

/**
 * Frontend-only decrypt for testing
 */
export async function decryptPassword(encrypted: string): Promise<string> {
  try {
    let decrypted = "";
    // Process hex string in pairs
    for (let i = 0; i < encrypted.length; i += 2) {
      const hexByte = encrypted.substring(i, i + 2);
      const xorByte = parseInt(hexByte, 16);
      const keyIdx = Math.floor(i / 2) % ENCRYPTION_KEY.length;
      const keyChar = ENCRYPTION_KEY.charCodeAt(keyIdx);
      decrypted += String.fromCharCode(xorByte ^ keyChar);
    }

    const parsed = JSON.parse(decrypted);
    return parsed.p;
  } catch (error) {
    console.error("Password decryption failed:", error);
    return "";
  }
}

/**
 * Hash password using built-in crypto API
 * This can be used for additional client-side verification
 */
export async function hashPassword(password: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    return hashHex;
  } catch (error) {
    console.error("Password hashing failed:", error);
    return "";
  }
}
